import React, { useRef, useEffect, forwardRef, useImperativeHandle } from 'react';
import { GeneratorSettings } from '../types';
import { renderCanvas } from '../lib/canvas';

interface ImageCanvasProps {
    settings: GeneratorSettings;
    isWatermarked: boolean;
    templateImage?: HTMLImageElement | null;
    presetImage?: HTMLImageElement | null;
    width?: number;
    height?: number;
    isLimited?: boolean;
}

export interface ImageCanvasRef {
    getCanvas: () => HTMLCanvasElement | null;
}

/**
 * Live-preview canvas component. Re-renders whenever settings change.
 * Supports both solid background and template image backgrounds.
 */
const ImageCanvas = forwardRef<ImageCanvasRef, ImageCanvasProps>(
    ({ settings, isWatermarked, templateImage, presetImage, width = 1080, height = 1080, isLimited = false }, ref) => {
        const canvasRef = useRef<HTMLCanvasElement>(null);

        // Expose canvas ref to parent
        useImperativeHandle(ref, () => ({
            getCanvas: () => canvasRef.current,
        }));

        // Re-render on any settings change
        useEffect(() => {
            let active = true;
            const canvas = canvasRef.current;
            if (!canvas) return;

            const draw = () => {
                if (active) {
                    renderCanvas(canvas, settings, isWatermarked, templateImage, presetImage);
                }
            };

            // Simple font loading check
            if ('fonts' in document) {
                // Load multiple font weights/styles if needed (specifically for script)
                const fontStr = `800 20px ${settings.font}`;
                const scriptFont = `600 20px 'Dancing Script'`;

                Promise.all([
                    document.fonts.load(fontStr),
                    document.fonts.load(scriptFont)
                ]).then(() => {
                    draw();
                }).catch(() => {
                    draw(); // Fallback to drawing anyway
                });
            } else {
                draw();
            }

            return () => {
                active = false;
            };
        }, [settings, isWatermarked, templateImage, presetImage]);

        const aspectRatio = (height / width) * 100;

        return (
            <div
                className="w-full max-w-lg mx-auto rounded-xl overflow-hidden relative bg-dark-950 group border border-white/5 shadow-2xl"
                style={{ paddingBottom: `${aspectRatio}%`, height: 0 }}
            >
                {/* ─── THE SHIELD: Transparent overlay to block inspect/right-click ─── */}
                <div
                    className="absolute inset-0 z-10 w-full h-full bg-transparent cursor-default"
                    onContextMenu={(e) => e.preventDefault()}
                />

                <canvas
                    ref={canvasRef}
                    width={width}
                    height={height}
                    className={`absolute inset-0 w-full h-full block select-none touch-none transition-all duration-500 ${isLimited ? 'blur-md grayscale opacity-50' : ''
                        }`}
                    style={{ WebkitUserSelect: 'none', WebkitTouchCallout: 'none' }}
                    onContextMenu={(e) => e.preventDefault()}
                    aria-label="Quote image preview"
                />

                {/* Optional: 'Unlock' message overlay when limited */}
                {isLimited && (
                    <div
                        className="absolute inset-0 z-20 flex flex-col items-center justify-center p-6 text-center bg-dark-950/40 backdrop-blur-sm animate-fade-in"
                        onContextMenu={(e) => e.preventDefault()}
                    >
                        <span className="text-4xl mb-3">🔒</span>
                        <p className="text-white font-bold text-sm uppercase tracking-widest">Daily Limit Reached</p>
                        <p className="text-dark-300 text-[10px] mt-1">Upgrade to unlock full resolution preview</p>
                    </div>
                )}
            </div>
        );
    }
);

ImageCanvas.displayName = 'ImageCanvas';

export default ImageCanvas;
